/*
  Demo unit request modal.

  Posts straight to the HubSpot Forms submission API instead of loading the
  HubSpot embed. The embed renders a cross-origin iframe, which costs an extra
  network round trip and cannot inherit the theme's styles.
*/

class DemoRequest extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.portalId = this.dataset.portalId;
    this.formId = this.dataset.formId;
    this.modal = this.querySelector('[data-demo-modal]');
    // The opener lives inside the product form, so it is a sibling rather than
    // a child. It is rendered ahead of this element, so it already exists.
    this.opener = document.querySelector(`[data-demo-open="${this.dataset.modalId}"]`);

    if (!this.opener || !this.modal || !this.portalId || !this.formId) return;

    // Move the modal to the body so `position: fixed` is measured against the
    // viewport. Inside the product form it can otherwise be trapped by an
    // ancestor with a transform or overflow.
    document.body.appendChild(this.modal);

    this.panel = this.modal.querySelector('[data-demo-panel]');
    this.intro = this.modal.querySelector('[data-demo-intro]');
    this.form = this.modal.querySelector('form');
    this.status = this.modal.querySelector('[data-demo-status]');
    this.success = this.modal.querySelector('[data-demo-success]');
    this.submitButton = this.modal.querySelector('[data-demo-submit]');

    this.opener.addEventListener('click', this.open.bind(this));
    this.modal.querySelectorAll('[data-demo-close]').forEach((el) => {
      el.addEventListener('click', this.close.bind(this));
    });
    this.modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  open() {
    // Reset in case this is a reopen after a prior successful submission.
    if (this.intro) this.intro.removeAttribute('hidden');
    this.form.removeAttribute('hidden');
    this.form.reset();
    if (this.success) this.success.setAttribute('hidden', '');
    this.setStatus('');

    this.modal.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');
    if (typeof trapFocus === 'function') {
      trapFocus(this.modal, this.panel);
    } else if (this.panel) {
      this.panel.focus();
    }
  }

  close() {
    this.modal.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.opener);
    } else {
      this.opener.focus();
    }
  }

  setStatus(message, isError) {
    if (!this.status) return;
    this.status.textContent = message || '';
    this.status.classList.toggle('demo-modal__status--error', Boolean(isError));
  }

  async onSubmit(event) {
    event.preventDefault();

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    if (this.submitting) return;
    this.submitting = true;

    const originalLabel = this.submitButton.textContent;
    this.submitButton.setAttribute('disabled', '');
    this.submitButton.textContent = this.submitButton.dataset.sendingLabel || 'Sending...';
    this.setStatus('');

    // Field names must match the HubSpot form definition exactly; HubSpot
    // rejects the whole submission if it sees a field the form does not define.
    // Names prefixed with an underscore are local-only and never sent.
    const fields = [];
    new FormData(this.form).forEach((value, name) => {
      const trimmed = String(value).trim();
      if (name.charAt(0) === '_' || trimmed === '') return;
      fields.push({ name, value: trimmed });
    });

    const hutk = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/);
    const payload = {
      fields,
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };
    if (hutk) payload.context.hutk = hutk[1];

    try {
      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${this.portalId}/${this.formId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(
          detail
            ? detail.message || (detail.errors || []).map((e) => e.message).join('; ')
            : `HTTP ${response.status}`
        );
      }

      this.form.setAttribute('hidden', '');
      if (this.intro) this.intro.setAttribute('hidden', '');
      if (this.success) this.success.removeAttribute('hidden');
    } catch (error) {
      console.error('[demo-request] HubSpot submission failed:', error);
      this.setStatus(
        this.form.dataset.errorMessage || 'Sorry, something went wrong. Please try again or call us.',
        true
      );
    } finally {
      this.submitting = false;
      this.submitButton.removeAttribute('disabled');
      this.submitButton.textContent = originalLabel;
    }
  }
}

customElements.define('demo-request', DemoRequest);
